import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Footer } from "./Sections2";
import { useContent } from "../../context/ContentContext";
import { usePrefs } from "../../context/PrefsContext";
import { api, fileUrl, trackConversion } from "../../lib/api";

export const PageBanner = ({ title, subtitle, image, video, posY }) => {
  const { t } = usePrefs();
  const hasMedia = !!(image || video);
  const objPos = { objectPosition: `center ${posY ?? 50}%` };
  return (
    <section
      className="relative pt-44 pb-28 lg:pt-52 lg:pb-36 border-b border-white/10 overflow-hidden bg-[#0b1533] min-h-[420px] lg:min-h-[520px] flex items-end"
      data-testid="page-banner"
    >
      {video && (
        <video
          src={fileUrl(video)}
          className="absolute inset-0 w-full h-full object-cover"
          style={objPos}
          autoPlay
          muted
          loop
          playsInline
          data-testid="page-banner-video"
        />
      )}
      {!video && image && (
        <img
          src={fileUrl(image)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={objPos}
          data-testid="page-banner-image"
        />
      )}
      {hasMedia && (
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1533]/95 via-[#0b1533]/70 to-[#0b1533]/30" />
      )}
      {hasMedia && (
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1533] via-transparent to-transparent" />
      )}
      <div className="relative max-w-7xl mx-auto px-6 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.banner.company}</span>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl mt-3 drop-shadow-lg">{title}</h1>
          {subtitle && <p className="text-slate-200 mt-4 max-w-2xl text-base lg:text-lg drop-shadow">{subtitle}</p>}
        </motion.div>
      </div>
    </section>
  );
};

const WhatsAppButton = ({ number }) => {
  if (!number) return null;
  const v = number.trim();
  let href;
  if (/^https?:\/\//i.test(v)) href = v;
  else if (v.toLowerCase().includes("wa.me")) href = `https://${v}`;
  else {
    const digits = v.replace(/[^0-9]/g, "");
    if (!digits) return null;
    href = `https://wa.me/${digits.startsWith("0") ? "6" + digits : digits}`;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      data-testid="whatsapp-float-btn"
      onClick={trackConversion}
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] shadow-lg shadow-black/25 flex items-center justify-center transition-transform hover:scale-110"
    >
      <svg viewBox="0 0 32 32" className="w-8 h-8 fill-white" aria-hidden="true">
        <path d="M16 3C9.373 3 4 8.373 4 15c0 2.31.655 4.467 1.789 6.297L4.06 27.94l6.816-1.694A11.94 11.94 0 0 0 16 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 21.818a9.77 9.77 0 0 1-4.98-1.362l-.357-.212-4.045 1.005 1.079-3.944-.233-.372A9.77 9.77 0 0 1 6.182 15c0-5.423 4.395-9.818 9.818-9.818S25.818 9.577 25.818 15 21.423 24.818 16 24.818zm5.384-7.348c-.295-.148-1.745-.861-2.015-.959-.27-.099-.467-.148-.664.148-.196.295-.762.958-.934 1.155-.172.197-.344.221-.639.074-.295-.148-1.246-.459-2.373-1.464-.877-.782-1.469-1.748-1.641-2.043-.172-.295-.018-.455.13-.602.132-.132.295-.344.443-.517.148-.172.196-.295.295-.492.098-.197.049-.369-.025-.517-.074-.148-.664-1.6-.909-2.19-.24-.576-.483-.498-.664-.507l-.566-.01c-.197 0-.516.074-.787.369-.27.295-1.032 1.008-1.032 2.46s1.057 2.853 1.204 3.05c.148.197 2.079 3.176 5.038 4.453.704.304 1.253.485 1.681.62.706.225 1.35.193 1.858.117.567-.085 1.745-.713 1.991-1.402.246-.688.246-1.278.172-1.401-.074-.123-.27-.197-.566-.345z" />
      </svg>
    </a>
  );
};

const PAGE_TITLES = {
  "/": "Tanjung Marina Global | Rakan Logistik Dipercayai di Malaysia",
  "/tentang-kami": "Tentang Kami | Tanjung Marina Global",
  "/pasukan-kami": "Pasukan Kami | Tanjung Marina Global",
  "/perkhidmatan": "Perkhidmatan Logistik | Tanjung Marina Global",
  "/galeri": "Galeri | Tanjung Marina Global",
  "/hubungi": "Hubungi Kami | Tanjung Marina Global",
};

export const SiteLayout = ({ children }) => {
  const content = useContent();
  const { t } = usePrefs();
  const location = useLocation();
  const lastTrackedRef = useRef({ path: "", at: 0 });

  useEffect(() => {
    document.title = PAGE_TITLES[location.pathname] || PAGE_TITLES["/"];
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    const now = Date.now();
    // Dedupe: skip if same path fired within the last 2 seconds (guards against StrictMode double-invoke)
    if (lastTrackedRef.current.path === location.pathname && now - lastTrackedRef.current.at < 2000) return;
    lastTrackedRef.current = { path: location.pathname, at: now };
    api.post("/analytics/track", {
      path: location.pathname,
      referrer: document.referrer || "",
      screen_w: window.innerWidth,
      screen_h: window.innerHeight,
    }).catch(() => {});
  }, [location.pathname]);

  if (!content)
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center text-slate-500 dark:text-slate-400" data-testid="site-loading">
        {t.misc.loading}
      </div>
    );

  return (
    <div className="bg-slate-50 dark:bg-slate-950 min-h-screen">
      <Header />
      <main>{typeof children === "function" ? children(content) : children}</main>
      <Footer contact={content.contact} about={content.about} />
      <WhatsAppButton number={content.contact?.whatsapp} />
    </div>
  );
};
